import { ISurvey } from './interfaces/ISurvey';

export const SURVEY_PREVIEW_DB_NAME = 'survey-preview';
export const SURVEY_PREVIEW_STORE_NAME = 'drafts';
export const SURVEY_PREVIEW_DRAFT_TTL_MS = 60 * 60 * 1000;

export interface ISurveyPreviewDraft {
  survey: ISurvey;
  type: string;
  savedSurveyId: string;
  fontDropped: boolean;
  createdAt: number;
}

export interface IDraftStorage {
  put(key: string, draft: ISurveyPreviewDraft): Promise<void>;
  get(key: string): Promise<ISurveyPreviewDraft | null>;
  prune(olderThanEpochMs: number): Promise<void>;
}

export const storeSurveyPreviewDraft = async (
  storage: IDraftStorage,
  key: string,
  draft: ISurveyPreviewDraft
): Promise<{ fontDropped: boolean }> => {
  try {
    await storage.prune(draft.createdAt - SURVEY_PREVIEW_DRAFT_TTL_MS);
  } catch {
    // A full or broken store must not block a preview.
  }

  try {
    await storage.put(key, draft);
    return { fontDropped: false };
  } catch (error) {
    if (!draft.survey.customFont) throw error;
    const withoutFont: ISurveyPreviewDraft = {
      ...draft,
      fontDropped: true,
      survey: { ...draft.survey, customFont: '', customFontFormat: '' },
    };
    await storage.put(key, withoutFont);
    return { fontDropped: true };
  }
};

const openDatabase = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(SURVEY_PREVIEW_DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(SURVEY_PREVIEW_STORE_NAME)) {
        request.result.createObjectStore(SURVEY_PREVIEW_STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const runTransaction = <T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>
) =>
  new Promise<T>((resolve, reject) => {
    openDatabase()
      .then((db) => {
        const transaction = db.transaction(SURVEY_PREVIEW_STORE_NAME, mode);
        const request = action(transaction.objectStore(SURVEY_PREVIEW_STORE_NAME));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        transaction.oncomplete = () => db.close();
        transaction.onerror = () => {
          db.close();
          reject(transaction.error);
        };
      })
      .catch(reject);
  });

export const indexedDbDraftStorage: IDraftStorage = {
  put: async (key, draft) => {
    await runTransaction('readwrite', (store) => store.put(draft, key));
  },
  get: async (key) => (await runTransaction('readonly', (store) => store.get(key))) ?? null,
  prune: async (olderThanEpochMs) => {
    await new Promise<void>((resolve, reject) => {
      openDatabase()
        .then((db) => {
          const transaction = db.transaction(SURVEY_PREVIEW_STORE_NAME, 'readwrite');
          const request = transaction.objectStore(SURVEY_PREVIEW_STORE_NAME).openCursor();
          request.onerror = () => reject(request.error);
          request.onsuccess = () => {
            const cursor = request.result;
            if (!cursor) return;
            if ((cursor.value as ISurveyPreviewDraft).createdAt < olderThanEpochMs) cursor.delete();
            cursor.continue();
          };
          transaction.oncomplete = () => {
            db.close();
            resolve();
          };
          transaction.onerror = () => {
            db.close();
            reject(transaction.error);
          };
        })
        .catch(reject);
    });
  },
};
